import React, { useCallback, useEffect, useRef, useState } from "react";
import { Archive, Check, Trash2, Mail, Phone, Undo2, Reply, Send, Inbox } from "lucide-react";
import { api, dateCourte } from "./api";
import { useConfirmation } from "./Confirmation";
import { Badge, Case, Modale, BarreSelection, BoutonSombre, ChampTexte, ATonAttention, pointsATraiter, EnTeteMenu, TONS_BLOC } from "./ui";

const FILTRES = [
  { id: "a_traiter", label: "À traiter" },
  { id: "traites", label: "Traités" },
  { id: "tous", label: "Tous" },
];

const TON = { Contact: "bleu", "Assurance SWICA": "ambre", "Cours privé": "vert", "Avis cours d'essai": "ambre" };

export default function Messages({ onChangement, cible, aTraiter }) {  const [data, setData] = useState(null);
  const [filtre, setFiltre] = useState("a_traiter");
  const [choisis, setChoisis] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const [reponse, setReponse] = useState(null);

  const charger = useCallback(async () => setData(await api("/messages")), []);
  useEffect(() => { charger(); }, [charger]);
  // Le message visé (« Voir le message dans la console » de l'e-mail à l'école) : déjà
  // traité, il est sous « Tous » ; il vient à l'écran une fois, encadré.
  const amene = useRef(false);
  useEffect(() => {
    if (!cible || !data || amene.current) return;
    const m = (data.items || []).find((x) => x.id === cible);
    if (!m) return;
    if (m.traite && filtre === "a_traiter") { setFiltre("tous"); return; }
    const el = [...document.querySelectorAll("[data-message-id]")].find((x) => x.getAttribute("data-message-id") === cible);
    if (el) { amene.current = true; el.scrollIntoView({ block: "center" }); }
  }, [cible, data, filtre]);

  const items = (data?.items || []).filter((m) =>
    filtre === "tous" ? true : filtre === "traites" ? m.traite : !m.traite
  );
  const sources = Object.fromEntries((data?.items || []).map((m) => [m.id, m.source]));

  const lot = async (action, ids) => {
    await api("/messages/lot", { method: "POST", body: JSON.stringify({ ids, action, sources }) });
    setChoisis([]);
    setConfirmation(null);
    await charger();
    onChangement();
  };

  const basculer = (m) => lot(m.traite ? "non_traite" : "traite", [m.id]);

  if (!data) return <p className="text-[var(--muted)]">Chargement…</p>;

  const tousChoisis = items.length > 0 && items.every((m) => choisis.includes(m.id));

  return (
    <div className="space-y-5" data-testid="console-messages">
      <EnTeteMenu ton="messages" Icon={Inbox} contexte="Formulaires du site" titre="Messages" testid="messages-entete"
        points={[
          [Inbox, "Ce qui arrive", "Contact, SWICA, cours privés, avis après l'essai"],
          [Reply, "Répondre", "D'ici ou depuis Gmail : le message passe « Traité »"],
          [Archive, "Gardés", "Sans limite, effacés seulement à la main"],
        ]}
        droite={data.a_traiter > 0
          ? <Badge ton="ambre" testid="messages-badge-a-traiter">{data.a_traiter} à traiter</Badge>
          : <Badge ton="vert" testid="messages-badge-a-traiter">Tout est traité</Badge>}>
        <div className="flex gap-1 p-1 rounded-full bg-white border" style={{ borderColor: TONS_BLOC.messages.bord }}>
          {FILTRES.map((f) => (
            <button
              key={f.id}
              data-testid={`messages-filtre-${f.id}`}
              onClick={() => { setFiltre(f.id); setChoisis([]); }}
              className="px-4 py-2 rounded-full text-xs font-semibold transition-colors"
              style={{
                background: filtre === f.id ? "#fff" : "transparent",
                color: filtre === f.id ? "var(--primary)" : "var(--muted)",
                boxShadow: filtre === f.id ? "var(--shadow-sm)" : "none",
              }}
            >
              {f.label}{f.id === "a_traiter" && data.a_traiter > 0 && ` (${data.a_traiter})`}
            </button>
          ))}
        </div>
      </EnTeteMenu>
      <ATonAttention testid="messages-attention"
        points={pointsATraiter(aTraiter, ["messages"], () => setFiltre("a_traiter"))} />

      {items.length > 0 && (
        <div className="flex items-center gap-3 px-1">
          <Case cochee={tousChoisis} onChange={(v) => setChoisis(v ? items.map((m) => m.id) : [])}
            testid="messages-tout-selectionner" titre="Tout sélectionner" />
          <span className="text-xs text-[var(--muted)]">Tout sélectionner ({items.length})</span>
        </div>
      )}

      {choisis.length > 0 && (
        <BarreSelection nb={choisis.length} testid="messages-barre-selection">
          <BoutonSombre onClick={() => lot("traite", choisis)} testid="messages-lot-traite">
            <Check size={14} /> Marquer traités
          </BoutonSombre>
          <BoutonSombre onClick={() => lot("non_traite", choisis)} testid="messages-lot-non-traite">
            <Undo2 size={14} /> Remettre à traiter
          </BoutonSombre>
          <BoutonSombre danger onClick={() => setConfirmation(choisis)} testid="messages-lot-supprimer">
            <Trash2 size={14} /> Supprimer
          </BoutonSombre>
        </BarreSelection>
      )}

      {items.length === 0 && (
        <p className="text-sm text-[var(--muted)] bg-white rounded-2xl border p-8 text-center" style={{ borderColor: "var(--border)" }}>
          Aucun message dans cette vue.
        </p>
      )}

      <div className="space-y-3">
        {items.map((m) => (
          <article
            key={m.id}
            data-testid="message-carte"
            data-message-id={m.id}
            className="bg-white rounded-2xl border p-5 flex gap-4 transition-shadow hover:shadow-[var(--shadow-sm)]"
            style={{ borderColor: cible === m.id ? "var(--primary)" : "var(--border)", borderWidth: cible === m.id ? 2 : 1 }}
          >
            <Case
              cochee={choisis.includes(m.id)}
              onChange={(v) => setChoisis(v ? [...choisis, m.id] : choisis.filter((x) => x !== m.id))}
              testid="message-case"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <Badge ton={TON[m.categorie] || "bleu"}>{m.categorie}</Badge>
                <span className="font-semibold">{m.nom}</span>
                {m.traite && <Badge ton="vert"><Check size={12} /> traité</Badge>}
                {/* Gmail (Réglages › Gmail, 02.11.2026) : répondu depuis Gmail, ou relance de la famille. */}
                {m.traite && m.repondu_gmail_le && <span className="text-xs" style={{ color: "#15803D" }} data-testid="message-gmail-repondu">Répondu depuis Gmail le {dateCourte(m.repondu_gmail_le)}</span>}
                {!m.traite && m.relance_gmail_le && <Badge ton="ambre">La famille a réécrit le {dateCourte(m.relance_gmail_le)}</Badge>}
                <span className="ml-auto text-xs text-[var(--muted)]">{dateCourte(m.recu_le)}</span>
              </div>
              <p className="text-sm mt-3 whitespace-pre-wrap">{m.resume || "—"}</p>
              {(m.reponses || []).map((r, k) => (
                <div key={k} className="mt-3 text-xs rounded-xl p-3" style={{ background: "#F0FDF4", color: "#15803D" }}
                  data-testid="message-reponse-envoyee">
                  <strong>Répondu le {dateCourte(r.le)}{r.depuis_gmail ? " depuis Gmail" : ""}</strong> — {r.sujet} <span style={{ opacity: 0.7 }}>(envoyé à {r.envoye_a})</span>
                  {r.corps && (
                    <p className="mt-2 whitespace-pre-wrap" style={{ color: "#166534" }} data-testid="message-reponse-corps">
                      {r.corps}
                    </p>
                  )}
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-[var(--muted)]">
                {m.email && <span className="flex items-center gap-1.5"><Mail size={13} /> {m.email}</span>}
                {m.telephone && <span className="flex items-center gap-1.5"><Phone size={13} /> {m.telephone}</span>}
                <div className="ml-auto flex gap-2">
                  {m.email && (
                    <button className="btn-ghost" data-testid="message-repondre"
                      onClick={() => setReponse({ message: m, sujet: `Re : ta demande à 1000 ARTS`, corps: "" })}>
                      <Reply size={13} /> Répondre
                    </button>
                  )}
                  <button className="btn-ghost" data-testid="message-traiter" onClick={() => basculer(m)}>
                    {m.traite ? <><Undo2 size={13} /> Remettre à traiter</> : <><Check size={13} /> Marquer traité</>}
                  </button>
                  <button className="btn-ghost" data-testid="message-supprimer"
                    style={{ color: "#B91C1C", borderColor: "#FECACA" }}
                    onClick={() => setConfirmation([m.id])}>
                    <Trash2 size={13} /> Supprimer
                  </button>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      {reponse && <Reponse etat={reponse} setEtat={setReponse} modeReel={data.mode_reel}
        adresseTest={data.adresse_test}
        onEnvoye={async () => { setReponse(null); await charger(); onChangement(); }} />}

      {confirmation && (
        <Modale titre="Supprimer définitivement ?" onFermer={() => setConfirmation(null)} testid="message-confirmation"
          sous_titre={`${confirmation.length} message(s) seront effacés de la base. Irréversible — à ne faire que sur demande de la personne.`}>
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={() => setConfirmation(null)} data-testid="message-annuler-suppression">Annuler</button>
            <BoutonSombre danger onClick={() => lot("supprimer", confirmation)} testid="message-confirmer-suppression">
              <Trash2 size={14} /> Supprimer
            </BoutonSombre>
          </div>
        </Modale>
      )}
    </div>
  );
}

function Reponse({ etat, setEtat, onEnvoye, modeReel, adresseTest }) {
  const { informer } = useConfirmation();
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");

  const envoyer = async () => {
    setEnvoi(true);
    setErr("");
    try {
      const r = await api(`/messages/${etat.message.id}/repondre`, {
        method: "POST",
        body: JSON.stringify({ sujet: etat.sujet, corps: etat.corps }),
      });
      await informer({
        titre: "Réponse envoyée", ton: "succes",
        texte: r.mode_reel ? `La famille a reçu ta réponse à ${r.envoye_a}.`
          : `MODE TEST : la réponse est partie à ${r.envoye_a}, pas à la famille.`,
      });
      onEnvoye();
    } catch (e) {
      setErr(e.message);
    }
    setEnvoi(false);
  };

  return (
    <Modale
      large
      titre={`Répondre à ${etat.message.nom}`}
      sous_titre={`${etat.message.email} · l'envoi passe par notifications.1000arts.ch`}
      onFermer={() => setEtat(null)}
      testid="message-reponse"
    >
      {!modeReel && (
        <div className="rounded-xl p-3 mb-4 text-xs" style={{ background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A" }}
          data-testid="reponse-mode-test">
          <strong>MODE TEST actif :</strong> aucun e-mail ne part — ni à la famille, ni sur une
          adresse de test. À basculer dans Réglages → Envoi des e-mails.
        </div>
      )}
      <div className="rounded-xl p-3 mb-4 text-xs" style={{ background: "#F8FAFC", color: "#475569" }}>
        <strong>Son message :</strong> {etat.message.resume || "—"}
      </div>
      <ChampTexte label="Objet" valeur={etat.sujet} onChange={(v) => setEtat({ ...etat, sujet: v })} testid="reponse-sujet" />
      <label className="block mb-4">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Message</span>
        <textarea
          className="input-field mt-1"
          rows={8}
          value={etat.corps}
          onChange={(e) => setEtat({ ...etat, corps: e.target.value })}
          data-testid="reponse-corps"
        />
      </label>
      {err && <p className="text-sm mb-3" style={{ color: "#B91C1C" }}>{err}</p>}
      <div className="flex gap-2">
        <button className="btn-ghost" onClick={() => setEtat(null)}>Annuler</button>
        <BoutonSombre onClick={envoyer} disabled={envoi || !etat.corps.trim()} testid="reponse-envoyer">
          <Send size={14} /> {envoi ? "Envoi…" : "Envoyer la réponse"}
        </BoutonSombre>
      </div>
    </Modale>
  );
}
